import { arrayRemove, arrayUnion, doc, runTransaction, type Firestore } from 'firebase/firestore';
import type { TripConfig } from '../types';

/**
 * Trip Admin roles.
 *
 * Every admin is a peer: the trip's creator is just its first admin. `ownerId`
 * survives as internal bookkeeping (account deletion, the rules' legacy paths)
 * and always points at a current admin — demoting whoever holds it hands it to
 * another admin, so the trip can never be left with nobody in charge.
 *
 * An invitee who hasn't joined has no place in adminIds (they may not even
 * have an account yet), so an admin invitation is recorded against their email
 * in `adminInviteEmails` and applied by their own accept.
 */

export type TripRole = 'admin' | 'traveller';
type RoleTrip = Pick<TripConfig, 'ownerId' | 'adminIds' | 'memberIds'> & Partial<Pick<TripConfig, 'inviteEmails' | 'adminInviteEmails'>>;
export type RoleTarget = { id: string } | { email: string };
export type RoleChanges = Partial<Pick<TripConfig, 'adminIds' | 'ownerId' | 'adminInviteEmails'>>;

/** Current admins: adminIds, plus ownerId for older trips that never listed it. */
export function tripAdminIds(trip: RoleTrip): string[] {
  return [...new Set([trip.ownerId, ...(trip.adminIds || [])].filter(Boolean))];
}

export const isTripAdmin = (trip: RoleTrip | null | undefined, uid: string | undefined): boolean =>
  !!trip && !!uid && tripAdminIds(trip).includes(uid);

/** Pending invitee invited as an admin. */
export const isAdminInvite = (trip: RoleTrip, email: string | undefined): boolean =>
  !!email && (trip.adminInviteEmails || []).includes(email.toLowerCase());

/** Thrown when a demotion would leave no admin: the caller must pick a successor. */
export class NeedsSuccessor extends Error {
  constructor() { super('Choose someone to take over as Trip Admin first.'); }
}

/**
 * The fields to write to give `target` the role `role`, computed against the
 * trip as it is now. `successor` is only consulted when the change would leave
 * no admin; it must be a joined member.
 */
export function roleChange(trip: RoleTrip, actorId: string, target: RoleTarget, role: TripRole, successor?: string): RoleChanges {
  if (!isTripAdmin(trip, actorId)) throw new Error('Only a Trip Admin can change roles.');

  if ('email' in target) {
    const email = target.email.toLowerCase();
    if (!(trip.inviteEmails || []).map(e => e.toLowerCase()).includes(email)) {
      throw new Error('This invitation is no longer active.');
    }
    const current = trip.adminInviteEmails || [];
    const next = role === 'admin' ? [...new Set([...current, email])] : current.filter(e => e !== email);
    return next.length === current.length && next.every(e => current.includes(e)) ? {} : { adminInviteEmails: next };
  }

  const members = trip.memberIds || [];
  if (!members.includes(target.id)) throw new Error('Only travellers who have joined can be made Trip Admin.');
  const admins = tripAdminIds(trip);

  if (role === 'admin') {
    return admins.includes(target.id) ? {} : { adminIds: [...admins, target.id] };
  }
  if (!admins.includes(target.id)) return {};

  let next = admins.filter(id => id !== target.id);
  if (!next.length) {
    if (!successor) throw new NeedsSuccessor();
    if (successor === target.id || !members.includes(successor)) {
      throw new Error('The new Trip Admin has to be someone who has joined the trip.');
    }
    next = [successor];
  }
  // ownerId follows the admins: kept if its holder still is one, else handed
  // to whoever made the change (if they still are), else the first remaining.
  const ownerId = next.includes(trip.ownerId) ? trip.ownerId : next.includes(actorId) ? actorId : next[0];
  return { adminIds: next, ...(ownerId !== trip.ownerId ? { ownerId } : {}) };
}

/**
 * Apply a role change against the trip as stored, not as this client last saw
 * it — two admins changing roles at once each see the other's result.
 * Returns what was written ({} when nothing needed to change).
 */
export async function writeTripRole(
  db: Firestore, tripId: string, actorId: string, target: RoleTarget, role: TripRole, successor?: string,
): Promise<RoleChanges> {
  return runTransaction(db, async transaction => {
    const ref = doc(db, 'trips', tripId);
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error('This trip is no longer available.');
    const changes = roleChange(snapshot.data() as RoleTrip, actorId, target, role, successor);
    if (Object.keys(changes).length) transaction.update(ref, changes);
    return changes;
  });
}

/**
 * The extra fields an accept writes when the invitation carried the admin
 * role. Spread into the accept's own update, alongside memberIds.
 */
export function acceptRoleFields(trip: RoleTrip, uid: string, email: string): Record<string, unknown> {
  if (!isAdminInvite(trip, email)) return {};
  // Sentinels, like the accept's own memberIds: arrayUnion, so a trip that
  // changed since it was read still gets exactly "existing + me".
  return { adminIds: arrayUnion(uid), adminInviteEmails: arrayRemove(email.toLowerCase()) };
}
